import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { ApiError, get, post, setCsrf, type Session } from "./api";
import { makeAbility } from "./ability";
import { Palette } from "./components/Palette";
import { Loading, Shell } from "./components/Shell";
import { Approvals } from "./screens/Approvals";
import { Board } from "./screens/Board";
import { Correspondence } from "./screens/Correspondence";
import { EventDetail } from "./screens/EventDetail";
import { Impact } from "./screens/Impact";
import { Login } from "./screens/Login";
import { Radar } from "./screens/Radar";
import { Shadow } from "./screens/Shadow";
import { Signoff } from "./screens/Signoff";
import { Audit, Pack, Programme, Settings } from "./screens/Simple";
import { Sources } from "./screens/Sources";

/**
 * The whole application is behind a session. On load it asks the server who
 * it is talking to; a 401 means nobody, and the sign-in page is shown. The
 * client never decides what a role may do — it draws what the server says
 * and every endpoint refuses on its own regardless.
 */
export function App({ accounts, product }: {
  accounts: { subject: string; roles: string }[];
  product: string;
}) {
  const [session, setSession] = useState<Session | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    get<Session>("/session")
      .then((s) => { setCsrf(s.csrf); setSession(s); })
      .catch((e) => { if (!(e instanceof ApiError && e.status === 401)) console.error(e); })
      .finally(() => setChecked(true));
  }, []);

  if (!checked) return <main><Loading /></main>;
  if (!session) {
    return <Login onSignedIn={setSession} accounts={accounts} product={product} />;
  }

  const ability = makeAbility(session);

  async function signOut() {
    try { await post("/logout"); } finally { setSession(null); }
  }

  return (
    <Shell session={session} onSignOut={signOut}>
      <Palette nav={session.nav} />
      <Routes>
        <Route path="/" element={<Navigate to="/radar" replace />} />
        <Route path="/radar" element={<Radar ability={ability} />} />
        <Route path="/board" element={<Board />} />
        <Route path="/shadow" element={<Shadow />} />
        <Route path="/correspondence" element={<Correspondence />} />
        <Route path="/programme" element={<Programme />} />
        <Route path="/impact" element={<Impact />} />
        <Route path="/pack" element={<Pack />} />
        <Route path="/approvals" element={<Approvals />} />
        <Route path="/signoff" element={<Signoff />} />
        <Route path="/sources" element={<Sources />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/audit" element={<Audit />} />
        <Route path="/events/:reference" element={<EventDetail ability={ability} />} />
        <Route path="*" element={<NotHere />} />
      </Routes>
    </Shell>
  );
}

function NotHere() {
  return (
    <>
      <h1>Not found</h1>
      <p className="standfirst">There is nothing at that address.</p>
    </>
  );
}
